import { ReactElement, useState } from 'react';

import { appStrings } from '@backrs_ui/common-ui/src/assets/appStrings';
import { AppIcon } from '@backrs_ui/common-ui/src/designSystem/icons/AppIcon';

import { Box, Chip, SxProps, Theme, Typography } from '@mui/material';

import { ROIModalCard } from './ROIModalCard';
import { InfoModal } from '../InfoModal';

const ROI_LINE_CHART_STRINGS = appStrings.modules.charts.returnOnInvestment;

const getChipIcon = ({
  total_roi,
  onClick,
}: {
  total_roi: number;
  onClick: (chipType: 'goodROI' | 'lowROI' | 'veryGoodROI') => void;
}): ReactElement => {
  const chipType = (() => {
    if (total_roi > 100) return 'veryGoodROI';
    if (total_roi > 0) return 'goodROI';

    return 'lowROI';
  })();

  return (
    <Chip
      variant="badge"
      size="md"
      type="leading"
      label={ROI_LINE_CHART_STRINGS.modalCards[chipType].title}
      icon={
        <AppIcon
          iconName={chipType === 'lowROI' ? 'ThumbsDown' : 'ThumbsUp'}
          diameter="20px"
          color={ROI_LINE_CHART_STRINGS.modalCards[chipType].chipColor}
        />
      }
      sx={chartChipSx}
      onClick={() => onClick(chipType)}
    />
  );
};

type ROILineChartHeaderProps = Readonly<{
  total_roi: number;
}>;

export const ROILineChartHeader = ({ total_roi }: ROILineChartHeaderProps) => {
  // for the full description modal
  const [isFullMuiDialogOpen, setIsFullMuiDialogOpen] = useState(false);
  // for the individual modal, shown when clicking a chip
  const [isIndividualModalOpen, setIsIndividualModalOpen] = useState<
    'goodROI' | 'lowROI' | 'veryGoodROI' | undefined
  >(undefined);

  return (
    <>
      {/** MODAL WITH ALL THREE DESCRIPTIONS
       *
       * we need two separate modals, otherwise we run into weird empty state issues when closing either of them
       *
       */}
      <InfoModal
        open={isFullMuiDialogOpen}
        onClose={() => setIsFullMuiDialogOpen(false)}
        headerContent={
          <Box>
            <Typography
              variant="text-lg-semibold"
              color={({ backrs }) => backrs.colors['Colors/Text/text-primary']}
            >
              {ROI_LINE_CHART_STRINGS.explanationTitle}
            </Typography>

            <Typography variant="text-sm-regular" sx={headerContentTextSx}>
              {ROI_LINE_CHART_STRINGS.explanationText.first}
              <Typography variant="text-sm-bold" component="span">
                {ROI_LINE_CHART_STRINGS.explanationText.second}
              </Typography>
              {ROI_LINE_CHART_STRINGS.explanationText.third}
            </Typography>
          </Box>
        }
        bodyContent={
          <Box sx={bodyContentSx}>
            {Object.values(ROI_LINE_CHART_STRINGS.modalCards).map((card) => (
              <ROIModalCard
                key={card.title}
                title={card.title}
                chipLabel={card.chipLabel}
                chipColor={card.chipColor}
                iconName={card.iconName}
                cardDescriptions={card.cardDescription}
                leftCardContent={{
                  title: card.leftCardContent.title,
                  description: card.leftCardContent.description,
                }}
                rightCardContent={{
                  title: card.rightCardContent.title,
                  description: card.rightCardContent.description,
                }}
              />
            ))}
          </Box>
        }
      />

      {/** CHART HEADER CONTENT */}
      <Box sx={titleContentSx}>
        <Box sx={titleSx}>
          <Typography variant="text-lg-semibold" component="div">
            {ROI_LINE_CHART_STRINGS.title}
          </Typography>

          <Box sx={tooltipIconSx} onClick={() => setIsFullMuiDialogOpen((prev) => !prev)}>
            <AppIcon iconName="HelpCircle" diameter="20px" color="Colors/Foreground/fg-quinary" />
          </Box>
        </Box>
        <Box sx={totalInvestmentSx}>
          <Typography variant="display-sm-semibold" component="div">
            {total_roi}%
          </Typography>

          {/** CHIP ON CHART AND INDIVIDUAL CARD MODAL */}
          {getChipIcon({ total_roi, onClick: setIsIndividualModalOpen })}
          {isIndividualModalOpen && (
            <InfoModal
              open={!!isIndividualModalOpen}
              onClose={() => setIsIndividualModalOpen(undefined)}
              width="sm"
              bodyContent={
                <Box sx={bodyContentSx}>
                  <ROIModalCard
                    isIndividualCard
                    iconName={ROI_LINE_CHART_STRINGS.modalCards[isIndividualModalOpen].iconName}
                    key={ROI_LINE_CHART_STRINGS.modalCards[isIndividualModalOpen].title}
                    title={ROI_LINE_CHART_STRINGS.modalCards[isIndividualModalOpen].title}
                    chipLabel={ROI_LINE_CHART_STRINGS.modalCards[isIndividualModalOpen].chipLabel}
                    chipColor={ROI_LINE_CHART_STRINGS.modalCards[isIndividualModalOpen].chipColor}
                    cardDescriptions={
                      ROI_LINE_CHART_STRINGS.modalCards[isIndividualModalOpen].cardDescription
                    }
                    leftCardContent={{
                      title:
                        ROI_LINE_CHART_STRINGS.modalCards[isIndividualModalOpen].leftCardContent
                          .title,
                      description:
                        ROI_LINE_CHART_STRINGS.modalCards[isIndividualModalOpen].leftCardContent
                          .description,
                    }}
                    rightCardContent={{
                      title:
                        ROI_LINE_CHART_STRINGS.modalCards[isIndividualModalOpen].rightCardContent
                          .title,
                      description:
                        ROI_LINE_CHART_STRINGS.modalCards[isIndividualModalOpen].rightCardContent
                          .description,
                    }}
                  />
                </Box>
              }
            />
          )}
        </Box>
      </Box>
    </>
  );
};

const titleContentSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-md'],
});

const totalInvestmentSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'row',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: backrs.spacing['spacing-lg'],
  mb: { xs: backrs.spacing['spacing-2xl'], sm: backrs.spacing['spacing-none'] },
});

const titleSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: { xs: 'flex-start', sm: 'center' },
  flexDirection: { xs: 'row', sm: 'row' },
  justifyContent: 'space-between',
  gap: backrs.spacing['spacing-sm'],
});

const tooltipIconSx: SxProps<Theme> = {
  cursor: 'pointer',
};

const chartChipSx: SxProps<Theme> = ({ typography, backrs }) => ({
  border: `1px solid ${backrs.colors['Colors/Border/border-primary']}`,
  cursor: 'pointer',
  '& .MuiChip-label': {
    ...typography['text-lg-medium'],
  },
});

const bodyContentSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-lg'],
});

const headerContentTextSx: SxProps<Theme> = {
  whiteSpace: 'pre-line',
};
